import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

type BillingChannel = "email" | "face" | "portal" | "other";
type BillingChannelSource = "bill_to" | "contract" | null;

interface BillingChannelDetail {
  id: number;
  updatedAt: string;
  billingPartnerCode: string | null;
  billingPartnerName: string | null;
  billingPartnerEmail: string | null;
  billingChannel: BillingChannel | null;
  billingEmailOverride: string | null;
  billingChannelNotes: string | null;
  requiresExternalCertificationConformity: boolean;
  billingChannelDefaultSource: BillingChannelSource;
  resolvedBillingEmail: string | null;
  billingStrategy?: string | null;
  billingRequirementMode?: string | null;
  economics?: {
    billingStrategy: string | null;
    billingRequirementMode: string | null;
  };
}

const CHANNEL_LABELS: Record<BillingChannel, string> = {
  email: "Email",
  face: "FACe",
  portal: "Portal",
  other: "Otro",
};

function errorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (message.includes("BILLING_CHANNEL_EMAIL_REQUIRED")) {
    return "El destinatario de facturación no tiene email de facturación. Añade ese dato antes de configurar el canal Email.";
  }
  if (message.includes("BILLING_CHANNEL_EMAIL_INVALID")) return "El email de facturación no es válido.";
  if (message.includes("BILLING_CHANNEL_REQUIRED")) return "Selecciona un canal de facturación para este contrato.";
  if (message.includes("CONTRACT_STALE_UPDATE")) return "El contrato cambió en otra sesión. Recarga y vuelve a intentarlo.";
  return "No se pudo guardar el canal de facturación.";
}

function billToLabel(detail: BillingChannelDetail): string {
  const code = detail.billingPartnerCode ? `[${detail.billingPartnerCode}] ` : "";
  return `${code}${detail.billingPartnerName ?? "Destinatario de facturación sin entidad"}`;
}

export function ContractBillingChannelSection({
  contractId,
  canRead = false,
  canWriteEconomic = false,
}: {
  contractId: number;
  canRead?: boolean;
  canWriteEconomic?: boolean;
}) {
  const detailQuery = trpc.customers.contracts.get.useQuery(
    { contractId },
    { enabled: !!contractId && canRead },
  );
  const detail = detailQuery.data as BillingChannelDetail | undefined;
  const update = trpc.customers.contracts.updateEconomics.useMutation();
  const utils = trpc.useUtils();

  const [channel, setChannel] = useState<BillingChannel | "">("");
  const [emailMode, setEmailMode] = useState<"bill_to" | "override">("bill_to");
  const [emailOverride, setEmailOverride] = useState("");
  const [notes, setNotes] = useState("");
  const [requiresExternalConformity, setRequiresExternalConformity] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail) return;
    setChannel(detail.billingChannel ?? "");
    setEmailOverride(detail.billingEmailOverride ?? "");
    setEmailMode(detail.billingEmailOverride ? "override" : "bill_to");
    setNotes(detail.billingChannelNotes ?? "");
    setRequiresExternalConformity(detail.requiresExternalCertificationConformity === true);
    setError(null);
  }, [
    detail?.id,
    detail?.updatedAt,
    detail?.billingChannel,
    detail?.billingEmailOverride,
    detail?.billingChannelNotes,
    detail?.requiresExternalCertificationConformity,
  ]);

  const billToEmail = detail?.billingPartnerEmail?.trim() || null;
  const normalizedOverride = emailOverride.trim() || null;
  const billingStrategy = detail?.economics?.billingStrategy ?? detail?.billingStrategy ?? null;
  const billingRequirementMode = detail?.economics?.billingRequirementMode ?? detail?.billingRequirementMode ?? null;
  const channelRequired = useMemo(() => {
    if (!detail) return false;
    if (billingStrategy === "non_billable" || billingRequirementMode === "no_billing_required") return false;
    return billingStrategy != null || billingRequirementMode != null;
  }, [billingRequirementMode, billingStrategy, detail]);
  const emailNeedsConfiguration = channel === "email" && !billToEmail;
  const missingRequiredChannel = channelRequired && channel === "";
  const resolvedEmail = channel === "email"
    ? (emailMode === "override" ? normalizedOverride : billToEmail)
    : null;

  if (!canRead) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No tienes permiso para consultar el canal de facturación.
        </CardContent>
      </Card>
    );
  }

  if (detailQuery.isLoading || !detail) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          {detailQuery.isError ? "No se pudo cargar el canal de facturación." : "Cargando canal de facturación…"}
        </CardContent>
      </Card>
    );
  }

  async function handleSave() {
    if (!canWriteEconomic || !detail) return;
    if (missingRequiredChannel) {
      setError("Selecciona un canal de facturación para este contrato.");
      return;
    }
    if (emailNeedsConfiguration) {
      setError("El destinatario de facturación no tiene email de facturación. Completa ese dato antes de guardar el canal Email.");
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({
        contractId,
        expectedUpdatedAt: detail.updatedAt,
        billingChannel: channel || null,
        billingEmailOverride: channel === "email" && emailMode === "override" ? normalizedOverride : null,
        billingChannelNotes: channel === "portal" || channel === "other" ? notes.trim() || null : null,
        requiresExternalCertificationConformity: requiresExternalConformity,
      });
      await utils.customers.contracts.get.invalidate({ contractId });
      toast.success("Canal de facturación guardado.");
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const destinationEmail = detail.billingChannel === "email" ? detail.resolvedBillingEmail : null;

  return (
    <Card className="min-w-0 max-w-full [overflow-wrap:anywhere] [&_[data-slot=select-trigger]]:w-full [&_[data-slot=select-trigger]]:min-w-0 [&_[data-slot=select-trigger]]:max-w-full [&_[data-slot=select-trigger][data-size]]:h-auto [&_[data-slot=select-trigger]]:min-h-10 [&_[data-slot=select-trigger]]:whitespace-normal [&_[data-slot=select-trigger]]:text-left [&_[data-slot=select-trigger]_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-trigger]_[data-slot=select-value]]:flex-1 [&_[data-slot=select-trigger]_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-trigger]_[data-slot=select-value]]:block [&_[data-slot=select-trigger]_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-trigger]_[data-slot=select-value]]:[overflow-wrap:anywhere]">
      <CardHeader className="min-w-0">
        <CardTitle className="text-base">Canal de facturación</CardTitle>
        <p className="text-sm text-muted-foreground">
          Facturar a: {billToLabel(detail)}
          {destinationEmail ? ` · Email: ${destinationEmail}` : ""}
        </p>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        <div className="space-y-1.5">
          <Label>Canal de facturación</Label>
          <Select
            value={channel}
            onValueChange={(value) => {
              setChannel(value as BillingChannel);
              if (value !== "email") setEmailOverride("");
              setError(null);
            }}
            disabled={!canWriteEconomic}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona canal" />
            </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-0 max-w-[calc(100vw-2rem)] whitespace-normal [overflow-wrap:anywhere] [&_[role=option]]:min-w-0 [&_[role=option]>span:last-child]:min-w-0 [&_[role=option]>span:last-child]:block">
              {(Object.keys(CHANNEL_LABELS) as BillingChannel[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {CHANNEL_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {channel === "email" && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-1.5">
              <Label>Destino de email</Label>
              <Select
                value={emailMode}
                onValueChange={(value) => {
                  setEmailMode(value as "bill_to" | "override");
                  setError(null);
                }}
                disabled={!canWriteEconomic}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-0 max-w-[calc(100vw-2rem)] whitespace-normal [overflow-wrap:anywhere] [&_[role=option]]:min-w-0 [&_[role=option]>span:last-child]:min-w-0 [&_[role=option]>span:last-child]:block">
                  <SelectItem value="bill_to" disabled={!billToEmail}>
                    Heredado del destinatario de facturación{billToEmail ? ` · ${billToEmail}` : " · sin email"}
                  </SelectItem>
                  <SelectItem value="override">Específico del contrato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {emailMode === "override" && (
              <div className="space-y-1.5">
                <Label htmlFor={`billing-email-override-${contractId}`}>Email del contrato</Label>
                <Input
                  id={`billing-email-override-${contractId}`}
                  type="email"
                  value={emailOverride}
                  onChange={(event) => {
                    setEmailOverride(event.target.value);
                    setError(null);
                  }}
                  disabled={!canWriteEconomic}
                  placeholder="facturacion@empresa.es"
                />
              </div>
            )}
            {!billToEmail && (
              <p className="text-sm text-amber-700">
                El destinatario de facturación no tiene email de facturación. La configuración del canal Email está bloqueada hasta completar ese dato.
              </p>
            )}
            {resolvedEmail && <p className="text-xs text-muted-foreground">Email resuelto: {resolvedEmail}</p>}
          </div>
        )}

        {(channel === "portal" || channel === "other") && (
          <div className="space-y-1.5">
            <Label htmlFor={`billing-channel-notes-${contractId}`}>
              {channel === "portal" ? "Referencia de plataforma" : "Notas del canal"}
            </Label>
            <Textarea
              id={`billing-channel-notes-${contractId}`}
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
                setError(null);
              }}
              disabled={!canWriteEconomic}
              placeholder={channel === "portal" ? "Ej. Plataforma de contratación…" : "Indica cómo se gestiona la facturación…"}
              maxLength={2000}
            />
          </div>
        )}

        <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
          <input type="checkbox" checked={requiresExternalConformity}
            onChange={(event) => setRequiresExternalConformity(event.target.checked)}
            disabled={!canWriteEconomic} className="mt-1" />
          <span>
            <span className="font-medium">Exigir conformidad económica externa para nuevas certificaciones</span>
            <span className="mt-1 block text-muted-foreground">Contabilidad registrará referencia e importe conformado y conciliará diferencias antes de habilitar la factura. No modifica certificaciones ni facturas históricas.</span>
          </span>
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {!canWriteEconomic && (
          <p className="text-sm text-muted-foreground">Solo un rol con permiso WRITE_ECONOMIC puede editar este dato.</p>
        )}
        {canWriteEconomic && (
          <Button
            type="button"
            onClick={handleSave}
            disabled={update.isPending || missingRequiredChannel || emailNeedsConfiguration}
          >
            {update.isPending ? "Guardando…" : "Guardar canal"}
          </Button>
        )}
        {detail.billingChannel && (
          <p className="text-xs text-muted-foreground">
            Canal actual: {CHANNEL_LABELS[detail.billingChannel]} · origen: {detail.billingChannelDefaultSource === "bill_to" ? "destinatario de facturación" : "contrato"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
